import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { AlertTriangle, Copy, FolderPlus, MoreHorizontal, Search } from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../components/DataTable";
import { Sparkline } from "../components/Sparkline";
import { TableSkeleton } from "../components/states";
import {
  useProjects,
  type ProjectListItem,
  type ProjectsFilters,
} from "../lib/projects";
import { isFairUseHintDue } from "../lib/fair-use";

function FairUseHint({ rejectionsToday }: { rejectionsToday: number }) {
  if (!isFairUseHintDue(rejectionsToday)) return null;
  return (
    <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-warning-bg px-1.5 py-0.5 text-[11px] font-medium text-warning">
      <AlertTriangle size={11} />
      Fair-Use heute {rejectionsToday}× angeschlagen
    </div>
  );
}

function useDebounced<T>(value: T, delay = 200): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} kopiert`),
    () => toast.error("Kopieren fehlgeschlagen"),
  );
}

export function ProjectsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [search, setSearch] = useState(params.get("q") ?? "");
  const debouncedSearch = useDebounced(search, 200);

  const status = (params.get("status") || "all") as ProjectsFilters["status"];
  const sort = (params.get("sort") || "name") as ProjectsFilters["sort"];
  const workspace = (params.get("workspace") ||
    "all") as ProjectsFilters["workspace"];

  // Sync debounced search to URL
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedSearch) next.set("q", debouncedSearch);
    else next.delete("q");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const filters = useMemo<ProjectsFilters>(
    () => ({ q: debouncedSearch, status, sort, workspace }),
    [debouncedSearch, status, sort, workspace],
  );

  const { data, isLoading } = useProjects(filters);
  const items = data?.items ?? [];

  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const columns = useMemo<ColumnDef<ProjectListItem>[]>(
    () => [
      {
        id: "name",
        header: "Projekt",
        cell: ({ row }) => {
          const p = row.original;
          return (
            <div>
              <Link
                to={`/projects/${p.id}/overview`}
                onClick={(e) => e.stopPropagation()}
                className="font-medium text-text-primary hover:text-text-link-hover"
              >
                {p.name}
              </Link>
              <div className="font-mono text-xs text-text-tertiary">
                Funnel-ID {p.funnelId}
              </div>
              <FairUseHint rejectionsToday={p.fairUseRejectionsToday} />
            </div>
          );
        },
      },
      {
        id: "subdomain",
        header: "Tracker-URL",
        cell: ({ row }) => {
          const p = row.original;
          const sub = p.subdomain ? `${p.subdomain}.dhc-track.io` : null;
          return (
            <div className="font-mono text-xs">
              {sub && <div className="text-text-secondary">{sub}</div>}
              {p.customHostname && (
                <div className="text-text-primary">{p.customHostname}</div>
              )}
              {!sub && !p.customHostname && (
                <span className="text-text-tertiary">—</span>
              )}
            </div>
          );
        },
      },
      {
        id: "events24h",
        header: () => <div className="text-right">Events 24h</div>,
        cell: ({ row }) => (
          <div className="text-right font-mono tabular-nums text-text-primary">
            {row.original.last24h.toLocaleString("de-DE")}
          </div>
        ),
        size: 110,
      },
      {
        id: "spark",
        header: "7 Tage",
        cell: ({ row }) => {
          const p = row.original;
          if (!p.bqStatsAvailable) {
            return <span className="text-xs text-text-tertiary">—</span>;
          }
          return (
            <Sparkline
              data={p.sparkline}
              ariaLabel={`7-Tage-Trend für ${p.name}`}
            />
          );
        },
        size: 110,
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusPill active={row.original.active} archived={row.original.archivedAt != null} />
        ),
        size: 90,
      },
      {
        id: "created",
        header: "Erstellt",
        cell: ({ row }) => (
          <span className="text-xs text-text-secondary">
            {new Date(row.original.createdAt).toLocaleDateString("de-DE")}
          </span>
        ),
        size: 110,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Aktionen</span>,
        cell: ({ row }) => (
          <RowMenu
            project={row.original}
            onOpen={() => navigate(`/projects/${row.original.id}/overview`)}
          />
        ),
        size: 40,
      },
    ],
    [navigate],
  );

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-6">
        <header className="flex items-baseline justify-between">
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
              Projekte
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
              Übersicht
              <span className="ml-3 font-mono text-base font-normal tabular-nums text-text-secondary">
                {data?.total ?? 0}
              </span>
            </h1>
          </div>
          <Link
            to="/projects/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-2 text-sm font-medium text-text-on-accent transition-colors hover:bg-accent-hover"
          >
            <FolderPlus size={14} /> Neues Projekt
          </Link>
        </header>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 sm:max-w-sm">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary"
            />
            <input
              type="search"
              placeholder="Suchen…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-9 w-full rounded-md border border-border-primary bg-bg-primary pl-8 pr-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-border-focus focus:outline-none"
            />
          </div>

          <Select
            value={status ?? "all"}
            onChange={(v) => updateParam("status", v)}
            options={[
              { v: "all", label: "Alle" },
              { v: "active", label: "Aktiv" },
              { v: "paused", label: "Pausiert" },
              { v: "archived", label: "Archiv" },
            ]}
          />
          <Select
            value={sort ?? "name"}
            onChange={(v) => updateParam("sort", v)}
            options={[
              { v: "name", label: "Sort: Name" },
              { v: "events24h", label: "Sort: Events 24h" },
              { v: "created", label: "Sort: Erstellt" },
            ]}
          />
          <Select
            value={workspace ?? "all"}
            onChange={(v) => updateParam("workspace", v)}
            options={[
              { v: "all", label: "Alle Workspaces" },
              { v: "agentur", label: "Agentur" },
              { v: "marketingabteilung", label: "marketingabteilung.ai" },
            ]}
          />
        </div>

        {isLoading ? (
          <TableSkeleton rows={4} rowHeight={56} />
        ) : (
          <DataTable
            data={items}
            columns={columns}
            emptyState={
              <div className="rounded-md border border-dashed border-border-primary bg-bg-secondary px-4 py-12 text-center">
                <p className="text-sm font-medium text-text-primary">
                  Keine Treffer
                </p>
                <p className="mt-1 text-sm text-text-secondary">
                  {debouncedSearch
                    ? `Kein Projekt enthält „${debouncedSearch}".`
                    : "Erstes Projekt anlegen →"}
                </p>
                {!debouncedSearch && (
                  <Link
                    to="/projects/new"
                    className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-text-on-accent hover:bg-accent-hover"
                  >
                    <FolderPlus size={14} /> Projekt anlegen
                  </Link>
                )}
              </div>
            }
          />
        )}
      </div>
    </div>
  );
}

function StatusPill({ active, archived }: { active: boolean; archived: boolean }) {
  if (archived) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border-primary px-2 py-0.5 text-xs font-medium text-text-tertiary">
        Archiviert
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        active
          ? "bg-success-bg text-success"
          : "bg-bg-tertiary text-text-secondary"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${active ? "bg-success" : "bg-text-tertiary"}`}
      />
      {active ? "Aktiv" : "Pausiert"}
    </span>
  );
}

function Select({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { v: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 rounded-md border border-border-primary bg-bg-primary px-2 text-sm text-text-primary focus:border-border-focus focus:outline-none"
    >
      {options.map((o) => (
        <option key={o.v} value={o.v}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function RowMenu({
  project,
  onOpen,
}: {
  project: ProjectListItem;
  onOpen: () => void;
}) {
  const trackerUrl = project.customHostname
    ? `https://${project.customHostname}/t.js`
    : project.subdomain
      ? `https://${project.subdomain}.dhc-track.io/t.js`
      : null;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label={`Aktionen für ${project.name}`}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary transition-colors hover:bg-bg-hover hover:text-text-primary"
        >
          <MoreHorizontal size={14} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-dropdown min-w-[180px] rounded-md border border-border-primary bg-bg-elevated p-1 shadow-md animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu.Item
            onSelect={onOpen}
            className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text-primary outline-none hover:bg-bg-hover"
          >
            Öffnen
          </DropdownMenu.Item>
          {trackerUrl && (
            <DropdownMenu.Item
              onSelect={() => copyToClipboard(trackerUrl, "Tracker-URL")}
              className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text-primary outline-none hover:bg-bg-hover"
            >
              <Copy size={12} /> Tracker-URL kopieren
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
